import {
  AnalyticsPermissions,
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
// redont-allow-dynamic-import: route-level code splitting for carved analytics package
import { returnsClaimsAnalyticsRoutes } from "@redotech/redo-merchant-analytics-app/analytics/returns-claims/route";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo, ReactNode } from "react";
import { matchPath, Navigate } from "react-router-dom";
import { registrationRoutes } from "../../../registration/route";
import {
  ReturnsBreadcrumbLabel,
  TranslatedBreadcrumbLabel,
  TranslatedBreadcrumbLayout,
} from "../../../return/returns-breadcrumb-label";
import {
  claimRoutes,
  fraudRoutes,
  recycleRoutes,
  returnRoutes,
  warrantyRoutes,
  withdrawalRoutes,
} from "../../../return/route";
import { abTestRoutes, inAbTestEditor } from "./ab-test-routes";
import { defaultChildRoute } from "./legacy-redirects";
import { ManagedReturnsTabRedirectGuard } from "./managed-returns-tab-redirect-guard";

const reportBreadcrumb = breadcrumbSlot();
const returnsRootBreadcrumb = breadcrumbSlot();
const returnsClaimsAnalyticsBreadcrumb = breadcrumbSlot();
const managedReturnsBreadcrumb = breadcrumbSlot();
const inboundShipmentsBreadcrumb = breadcrumbSlot();
const inboundShipmentDetailBreadcrumb = breadcrumbSlot();
const serviceRequestsBreadcrumb = breadcrumbSlot();
const managedInventoryBreadcrumb = breadcrumbSlot();
const managedProductsBreadcrumb = breadcrumbSlot();
const managedTasksBreadcrumb = breadcrumbSlot();
const managedReturnsAnalyticsBreadcrumb = breadcrumbSlot();
const managedReturnsAnalyticsPageBreadcrumb = breadcrumbSlot();
const reportsBreadcrumb = breadcrumbSlot();
const customReportsBreadcrumb = breadcrumbSlot();

const RETURNS_ROUTE_BASE = "/stores/:storeId/returns";

const HIDE_PADDING_ROUTES: string[] = [
  "/returns/*",
  "/registrations/*",
  "/claims/*",
  "/warranties/*",
  "/recycle/*",
  "/withdrawals/*",
  "/fraud/*",
  "/analytics/returns-claims",
  "/analytics/returns-claims/*",
  "/analytics/managed-returns",
  "/analytics/reports",
  "/analytics/reports/*",
  "/managed-returns/*",
];

function hidePadding(url: string): boolean {
  return (
    inAbTestEditor(url) ||
    HIDE_PADDING_ROUTES.some(
      (route) => !!matchPath(`${RETURNS_ROUTE_BASE}${route}`, url),
    )
  );
}

const ManagedReturnsAccessGuard = memo(function ManagedReturnsAccessGuard({
  children,
}: {
  children: ReactNode;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  const isManagedReturnsEnabled = team.settings.redoManagedReturns?.enabled;

  if (!isManagedReturnsEnabled) {
    return <Navigate replace to={routes.returnsHref} />;
  }

  return <>{children}</>;
});

export const returnsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: returnsRootBreadcrumb },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <>
            <ReturnsBreadcrumbLabel breadcrumb={returnsRootBreadcrumb} />
            <Page
              hideHeader={inAbTestEditor}
              hideNavbar={inAbTestEditor}
              hidePadding={hidePadding}
              hideSearchBarAndBanner={inAbTestEditor}
            />
          </>
        ),
      };
    },
    children: [
      defaultChildRoute("returns"),
      {
        path: "warranties",
        children: warrantyRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY] },
      },
      {
        path: "returns",
        children: returnRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
      },
      {
        path: "claims",
        children: claimRoutes,
        handle: {
          permission: [
            ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
            ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
          ],
        },
      },
      {
        path: "registrations",
        children: registrationRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.MANAGE_WARRANTY] },
      },
      {
        path: "recycle",
        children: recycleRoutes,
        handle: {
          permission: [ReturnsAndClaimsPermissions.MANAGE_RECYCLING_REQUESTS],
        },
      },
      {
        path: "withdrawals",
        children: withdrawalRoutes,
        handle: {
          permission: [ReturnsAndClaimsPermissions.MANAGE_WITHDRAWAL_REQUESTS],
        },
      },
      ...abTestRoutes,
      {
        path: "fraud",
        children: fraudRoutes,
        handle: { permission: [ReturnsAndClaimsPermissions.VIEW_RETURN] },
      },
      {
        path: "analytics/returns-claims",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={returnsClaimsAnalyticsBreadcrumb}
            getLabel={(t) => t("Analytics")}
          />
        ),
        handle: {
          breadcrumb: returnsClaimsAnalyticsBreadcrumb,
          breadcrumbNavigate: false,
        },
        children: returnsClaimsAnalyticsRoutes,
      },
      {
        path: "managed-returns",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={managedReturnsBreadcrumb}
            getLabel={(t) => t("Managed returns")}
          />
        ),
        handle: {
          breadcrumb: managedReturnsBreadcrumb,
          breadcrumbNavigate: false,
        },
        children: [
          defaultChildRoute("inbound-shipments"),
          {
            path: "inbound-shipments",
            element: (
              <TranslatedBreadcrumbLayout
                breadcrumb={inboundShipmentsBreadcrumb}
                getLabel={(t) => t("Inbound Shipments")}
              />
            ),
            handle: { breadcrumb: inboundShipmentsBreadcrumb },
            children: [
              {
                index: true,
                async lazy() {
                  const { InboundShipmentsPage } =
                    // redont-allow-dynamic-import: route-level code splitting
                    await import("@redotech/redo-merchant-managed-returns-app/pages/inbound-shipments-page");
                  return {
                    Component: () => (
                      <ManagedReturnsAccessGuard>
                        <InboundShipmentsPage />
                      </ManagedReturnsAccessGuard>
                    ),
                  };
                },
              },
              {
                path: ":consolidationId",
                handle: {
                  breadcrumb: inboundShipmentDetailBreadcrumb,
                  hideHeader: true,
                },
                async lazy() {
                  const { InboundConsolidationDetailPage } =
                    // redont-allow-dynamic-import: route-level code splitting
                    await import("@redotech/redo-merchant-managed-returns-app/pages/inbound-consolidation-detail-page");
                  return {
                    Component: () => (
                      <ManagedReturnsAccessGuard>
                        <TranslatedBreadcrumbLabel
                          breadcrumb={inboundShipmentDetailBreadcrumb}
                          getLabel={(t) => t("Shipment")}
                        />
                        <InboundConsolidationDetailPage />
                      </ManagedReturnsAccessGuard>
                    ),
                  };
                },
              },
            ],
          },
          {
            path: "service-requests",
            handle: { breadcrumb: serviceRequestsBreadcrumb },
            async lazy() {
              const { ItemWorkRequestsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-fulfillment-app/wms/item-work-requests/item-work-requests-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <TranslatedBreadcrumbLabel
                        breadcrumb={serviceRequestsBreadcrumb}
                        getLabel={(t) => t("Service requests")}
                      />
                      <ItemWorkRequestsPage variant="merchant" />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
          {
            path: "inventory",
            handle: { breadcrumb: managedInventoryBreadcrumb },
            async lazy() {
              const { InventoryPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-managed-returns-app/pages/inventory-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <TranslatedBreadcrumbLabel
                        breadcrumb={managedInventoryBreadcrumb}
                        getLabel={(t) => t("Inventory")}
                      />
                      <InventoryPage />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
          {
            path: "products",
            handle: { breadcrumb: managedProductsBreadcrumb },
            async lazy() {
              const { ProductsPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-managed-returns-app/pages/products-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <TranslatedBreadcrumbLabel
                        breadcrumb={managedProductsBreadcrumb}
                        getLabel={(t) => t("Products")}
                      />
                      <ProductsPage />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
          {
            path: "tasks",
            handle: { breadcrumb: managedTasksBreadcrumb },
            async lazy() {
              const { TasksPage } =
                // redont-allow-dynamic-import: route-level code splitting
                await import("@redotech/redo-merchant-managed-returns-app/pages/tasks-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <TranslatedBreadcrumbLabel
                        breadcrumb={managedTasksBreadcrumb}
                        getLabel={(t) => t("Tasks")}
                      />
                      <TasksPage />
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
        ],
      },
      {
        path: "analytics/managed-returns",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={managedReturnsAnalyticsBreadcrumb}
            getLabel={(t) => t("Analytics")}
          />
        ),
        handle: {
          breadcrumb: managedReturnsAnalyticsBreadcrumb,
          breadcrumbNavigate: false,
        },
        children: [
          {
            index: true,
            handle: { breadcrumb: managedReturnsAnalyticsPageBreadcrumb },
            async lazy() {
              const { RedoManagedReturnsPage } =
                await import("@redotech/redo-merchant-managed-returns-app/redo-managed-returns-page");
              return {
                Component: () => {
                  return (
                    <ManagedReturnsAccessGuard>
                      <TranslatedBreadcrumbLabel
                        breadcrumb={managedReturnsAnalyticsPageBreadcrumb}
                        getLabel={(t) => t("Managed returns")}
                      />
                      <ManagedReturnsTabRedirectGuard>
                        <RedoManagedReturnsPage />
                      </ManagedReturnsTabRedirectGuard>
                    </ManagedReturnsAccessGuard>
                  );
                },
              };
            },
          },
        ],
      },
      {
        path: "analytics/reports",
        element: (
          <TranslatedBreadcrumbLayout
            breadcrumb={reportsBreadcrumb}
            getLabel={(t) => t("Reports")}
          />
        ),
        handle: {
          breadcrumb: reportsBreadcrumb,
          permission: OrderManagementPermissions.VIEW_REPORTS,
        },
        children: [
          {
            index: true,
            handle: {
              breadcrumb: reportBreadcrumb,
              permission: OrderManagementPermissions.VIEW_REPORTS,
            },
            async lazy() {
              const { ReportPage } =
                // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                await import("@redotech/redo-merchant-analytics-app/analytics/reports/report");
              return { element: <ReportPage breadcrumb={reportBreadcrumb} /> };
            },
          },
          {
            path: "custom",
            element: (
              <TranslatedBreadcrumbLayout
                breadcrumb={customReportsBreadcrumb}
                getLabel={(t) => t("Custom reports")}
              />
            ),
            handle: {
              breadcrumb: customReportsBreadcrumb,
              permission: AnalyticsPermissions.VIEW_ANALYTICS,
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { CustomAnalyticsReportsRoute } =
                    // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                    await import("@redotech/redo-merchant-analytics-app/analytics/reports/custom/custom-analytics-reports-route");
                  return { element: <CustomAnalyticsReportsRoute /> };
                },
              },
              {
                path: ":customReportId",
                handle: { hideHeader: true },
                async lazy() {
                  const { CustomAnalyticsReportPage } =
                    // redont-allow-dynamic-import: route-level code splitting for carved analytics package
                    await import("@redotech/redo-merchant-analytics-app/analytics/reports/custom/custom-analytics-report-page");
                  return { element: <CustomAnalyticsReportPage /> };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
